<script setup lang="ts">
/**
 * Top bar above the main content column.
 *
 * - Left: hamburger (mobile only) + current route title from `meta.title`.
 * - Right: Telegram community link, balance pill, logout.
 *
 * Uses a parent-managed `drawerOpen` ref via `toggle-sidebar` emit
 * (App.vue owns the state; we just signal the click).
 */
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useBalanceStore } from '../stores/balance';
import { ngonkaToGnk } from '../lib/format';

defineProps<{ drawerOpen: boolean }>();
defineEmits<{ (e: 'toggle-sidebar'): void }>();

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const balance = useBalanceStore();

onMounted(() => {
  if (!balance.data && !balance.loading) void balance.load();
});

const title = computed(() => (route.meta.title as string | undefined) ?? '');
const balanceGnk = computed(() =>
  balance.data ? `${ngonkaToGnk(balance.data.balance_ngonka)} GNK` : '— GNK',
);

async function handleLogout(): Promise<void> {
  await auth.logout();
  await router.push({ name: 'login' });
}
</script>

<template>
  <header
    class="border-b border-hairline px-4 lg:px-6 py-3 flex items-center justify-between gap-3 bg-canvas sticky top-0 z-30"
  >
    <div class="flex items-center gap-3 min-w-0">
      <button
        type="button"
        class="lg:hidden p-2 -ml-2 rounded-full text-muted hover:text-ink hover:bg-surface-soft focus-ring"
        :aria-expanded="drawerOpen"
        aria-controls="main-sidebar"
        aria-label="Toggle navigation"
        @click="$emit('toggle-sidebar')"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <h1 class="font-display text-base font-semibold truncate">{{ title }}</h1>
    </div>

    <div class="flex items-center gap-2 sm:gap-3 shrink-0">
      <a
        href="https://t.me/gonka_ai"
        target="_blank"
        rel="noopener noreferrer"
        class="btn-ghost text-xs px-3 py-1.5 focus-ring"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"
          />
        </svg>
        <span class="hidden sm:inline">Community</span>
      </a>
      <span class="pill text-ink">{{ balanceGnk }}</span>
      <button class="btn-ghost text-xs px-3 py-1.5 focus-ring" @click="handleLogout">
        Logout
      </button>
    </div>
  </header>
</template>
